import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Download, Loader2, Check, Link2, Search as SearchIcon, Music } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Apple Music playlist import.
 *
 * Two ways to import:
 *   1. Paste an Apple Music playlist URL (canonical or bare `pl.xxxxx` id).
 *   2. Search Apple Music's editorial + curator-made playlists by name and
 *      pick one from the result grid.
 *
 * Both paths resolve to the same backend endpoint that returns the playlist's
 * tracks normalised to the shape our queue + DJ Console already consume
 * ({id (Apple catalog song id), name, artists[], album_image, duration_ms}).
 */
const AppleMusicPlaylistImport = ({ onImport }) => {
  const [url, setUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(false);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pickingId, setPickingId] = useState(null);
  const debounceRef = useRef(null);

  // Debounced community-playlist search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = query.trim();
    if (!term) { setResults([]); return undefined; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${API}/music/apple-playlists/search`, { params: { q: term, limit: 12 } });
        setResults(r.data?.playlists || []);
      } catch (e) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  // Shared import path used by both URL paste + search-result pick.
  const importFrom = async (urlOrId, opts = {}) => {
    const { onSuccess } = opts;
    try {
      const response = await axios.get(`${API}/music/apple-playlists/tracks`, {
        params: { url: urlOrId },
      });
      const tracks = response.data?.tracks || [];
      const pl = response.data?.playlist || {};
      if (tracks.length === 0) {
        toast.error('Playlist has no playable tracks');
        return false;
      }
      const withBpm = tracks.filter((t) => (Number(t.bpm) || 0) > 0).length;
      onImport({
        name: pl.name || 'Imported playlist',
        description: pl.description || `${tracks.length} tracks from Apple Music`,
        cover_image: pl.image || tracks[0]?.album_image || '',
        tracks,
        source_playlist_id: pl.id || urlOrId,
      });
      let bpmHint = '';
      if (withBpm === tracks.length && withBpm > 0) {
        bpmHint = ` · sorted slow → fast by BPM`;
      } else if (withBpm > 0) {
        bpmHint = ` · ${withBpm}/${tracks.length} sorted by BPM, rest at end`;
      } else if (tracks.length > 3) {
        // Very new tracks (2025-2026 chart hits) usually don't have BPM
        // analysis stored yet on our tempo source. This isn't a bug.
        bpmHint = ` · BPM data not yet available for these tracks`;
      }
      toast.success(`Imported ${tracks.length} tracks from "${pl.name || 'Apple Music playlist'}"${bpmHint}`);
      onSuccess && onSuccess();
      return true;
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to import playlist';
      toast.error(msg);
      return false;
    }
  };

  const handleImportUrl = async () => {
    const trimmed = url.trim();
    if (!trimmed) {
      toast.error('Paste an Apple Music playlist URL first');
      return;
    }
    setImporting(true);
    const ok = await importFrom(trimmed, { onSuccess: () => setImported(true) });
    if (!ok) setImported(false);
    setImporting(false);
  };

  const handlePickResult = async (pl) => {
    setPickingId(pl.id);
    await importFrom(pl.id);
    setPickingId(null);
  };

  return (
    <div data-testid="apple-music-playlist-import" className="space-y-4">
      {/* URL paste */}
      <div>
        <div className="flex gap-2 mb-2">
          <div className="relative flex-1">
            <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#606060]" />
            <Input
              value={url}
              onChange={(e) => { setUrl(e.target.value); setImported(false); }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleImportUrl(); }}
              placeholder="Paste Apple Music playlist URL…"
              className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-[#606060]"
              data-testid="apple-playlist-url-input"
            />
          </div>
          <Button
            onClick={handleImportUrl}
            disabled={!url.trim() || importing || imported}
            className={imported
              ? 'bg-[#00E676]/20 text-[#00E676]'
              : 'bg-[#FF007F] hover:bg-[#CC0066] text-white'
            }
            data-testid="apple-playlist-import-button"
          >
            {importing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : imported ? (
              <><Check className="w-4 h-4 mr-1" /> Done</>
            ) : (
              <><Download className="w-4 h-4 mr-1" /> Import</>
            )}
          </Button>
        </div>
        <p className="text-[#606060] text-[10px]">
          Paste e.g. <span className="font-mono">music.apple.com/in/playlist/.../pl.xxxxxxxx</span>
        </p>
      </div>

      {/* Community playlist search */}
      <div className="border-t border-white/5 pt-4">
        <div className="flex items-center gap-2 mb-2">
          <SearchIcon className="w-4 h-4 text-[#606060]" />
          <p className="text-[12px] font-extrabold text-white uppercase tracking-[0.18em]">
            Or search Apple Music community playlists
          </p>
        </div>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="e.g. Today's Hits, Punjabi Bangers, Bollywood Wedding…"
          className="bg-white/5 border-white/10 text-white placeholder:text-[#606060]"
          data-testid="apple-playlist-search-input"
        />
        <div className="mt-3">
          {searching && (
            <div className="flex items-center gap-2 text-[#606060] text-xs">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…
            </div>
          )}
          {!searching && query.trim() && results.length === 0 && (
            <p className="text-[#606060] text-xs">No playlists found. Try a different search term.</p>
          )}
          {results.length > 0 && (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[280px] overflow-y-auto pr-1"
              data-testid="apple-playlist-results"
            >
              {results.map((pl) => {
                const busy = pickingId === pl.id;
                return (
                  <button
                    key={pl.id}
                    type="button"
                    onClick={() => !busy && handlePickResult(pl)}
                    disabled={busy}
                    className="flex items-center gap-3 p-2 rounded-lg bg-white/[0.03] hover:bg-white/[0.08] border border-white/5 text-left transition-colors disabled:opacity-50"
                    data-testid={`apple-playlist-result-${pl.id}`}
                  >
                    {pl.image ? (
                      <img
                        src={pl.image}
                        alt=""
                        className="w-12 h-12 rounded object-cover flex-shrink-0"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded bg-white/5 flex items-center justify-center flex-shrink-0">
                        <Music className="w-5 h-5 text-gray-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-semibold truncate">{pl.name}</p>
                      <p className="text-gray-500 text-[11px] truncate">
                        {pl.owner}{pl.track_count ? ` · ${pl.track_count} songs` : ''}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      {busy ? (
                        <Loader2 className="w-4 h-4 animate-spin text-[#FF007F]" />
                      ) : (
                        <Download className="w-4 h-4 text-[#FF007F]" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppleMusicPlaylistImport;
